import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ContentSearchResponse,
  COUNTRY_SECTIONS,
  SearchResult,
  SearchSection,
} from '@shared/models/search-section.model';
import { Observable } from 'rxjs';
import { InvestmentJurisdiction } from 'src/app/modules/admin/models';
import { API_BASE_URL } from 'src/app/web-api-client';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  readonly countrySections: SearchSection[] = COUNTRY_SECTIONS;

  getAvailableSections(isAdmin: boolean): SearchSection[] {
    return isAdmin ? [...COUNTRY_SECTIONS] : COUNTRY_SECTIONS;
  }

  /**
   * Returns matching country and section results with routes built from the
   * related country's ID. Inactive jurisdictions are skipped.
   */
  lightSearch(
    query: string,
    countries: InvestmentJurisdiction[],
    isAdmin: boolean
  ): SearchResult[] {
    const normalised = query.trim().toLowerCase();
    if (normalised.length < 2) return [];

    const tokens = normalised.split(/\s+/).filter(t => t.length > 0);
    const sections = this.getAvailableSections(isAdmin);
    const results: SearchResult[] = [];
    const addedRoutes = new Set<string>();

    const allTokensMatch = (text: string): boolean =>
      tokens.every(token => text.includes(token));

    const addResult = (label: string, route: string): void => {
      if (!addedRoutes.has(route)) {
        addedRoutes.add(route);
        results.push({ label, route });
      }
    };

    for (const country of countries) {
      const countryId = country.country?.countryId;
      if (countryId === undefined || !country.isActive) continue;

      for (const section of sections.filter(s => s.requiresCountry)) {
        const combined = `${country.name.toLowerCase()} ${section.name.toLowerCase()}`;
        if (allTokensMatch(combined)) {
          const route = section.routerLink.replace(
            '{countryId}',
            String(countryId)
          );
          addResult(`${country.name} — ${section.name}`, route);
        }
      }
    }

    return results.slice(0, 200);
  }

  /**
   * Deep-content search: calls the BE endpoint that searches inside the HTML content
   * of all GeneralCountryInformation records the user has access to.
   * Results are limited to 50 server-side.
   */
  searchContent(
    query: string,
    clientId?: number
  ): Observable<ContentSearchResponse> {
    let params = new HttpParams().set('q', query.trim());
    if (clientId !== undefined && clientId !== null) {
      params = params.set('clientId', clientId);
    }
    return this.http.get<ContentSearchResponse>(
      `${this.baseUrl}/api/v1/generalcountryinformation/search`,
      { params }
    );
  }
}
